// CSVUploadModal.jsx
// Standalone CSV upload + preview modal. Calls onImport(rows) when confirmed.

import React, { useState, useRef, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField,
  InputAdornment, Alert, Chip, Stack,
} from "@mui/material";
import {
  Close as CloseIcon, Download as DownloadIcon, UploadFile as UploadFileIcon,
  TableChart as TableChartIcon, EditNote as EditNoteIcon,
  ArrowForward as ArrowForwardIcon, Email as EmailIcon, Phone as PhoneIcon,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { toast } from "sonner";
import { parseCSV, csvTemplateUrl } from "./csv.utils";

export function CSVUploadModal({ open, onClose, onImport }) {
  const theme         = useTheme();
  const fileInputRef  = useRef(null);
  const [isDragOver,  setIsDragOver]  = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [editedRows,  setEditedRows]  = useState([]);
  const [fileName,    setFileName]    = useState("");

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.match(/\.csv$/i) && file.type !== "text/csv") { toast.error("Please upload a .csv file."); return; }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const r = parseCSV(e.target.result);
      setParseResult(r);
      setEditedRows(r.rows.map((row) => ({ ...row })));
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = (e) => { e.preventDefault(); setIsDragOver(false); handleFile(e.dataTransfer.files[0]); };
  const updateRowAmount = (i, val) => setEditedRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, amount: val, needsAmount: false } : r)));

  const handleConfirm = () => {
    const invalid = editedRows.filter((r) => !r.amount || parseFloat(r.amount) <= 0);
    if (invalid.length) { toast.warning(`Fill in amounts for: ${invalid.map((r) => r.name).join(", ")}.`); return; }
    onImport(editedRows); handleClose();
  };

  const handleClose = () => { setParseResult(null); setEditedRows([]); setFileName(""); onClose(); };

  const downloadTemplate = () => {
    const url = csvTemplateUrl();
    const a   = document.createElement("a"); a.href = url; a.download = "blitz_transfer_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const totalReady   = editedRows.filter((r) => r.amount && parseFloat(r.amount) > 0).length;
  const totalMissing = editedRows.filter((r) => !r.amount || parseFloat(r.amount) <= 0).length;
  const grandTotal   = editedRows.reduce((acc, r) => acc + parseFloat(r.amount || 0), 0);
  const parseErrors  = parseResult?.errors || [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2.25, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid", borderColor: "grey.200" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <TableChartIcon sx={{ color: "primary.main", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}>Bulk CSV Import</Typography>
              <Typography variant="caption" color="text.secondary">{fileName || "Import many recipients at once"}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: 13 }} />} onClick={downloadTemplate} sx={{ fontSize: "0.73rem", textTransform: "none" }}>Template</Button>
            <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {!parseResult && (
          <Box sx={{ p: 3 }}>
            <Box
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{ border: "2px dashed", borderColor: isDragOver ? "primary.main" : alpha(theme.palette.primary.main, 0.3), borderRadius: 3, p: { xs: 5, md: 7 }, textAlign: "center", cursor: "pointer", transition: "all 0.2s", "&:hover": { borderColor: "primary.main", bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
            >
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" hidden onChange={(e) => handleFile(e.target.files[0])} />
              <UploadFileIcon sx={{ fontSize: 36, color: "primary.main", mb: 1.5, opacity: 0.8 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: "0.95rem" }}>{isDragOver ? "Drop it!" : "Drop CSV here"}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>or <Box component="span" sx={{ color: "primary.main", fontWeight: 700 }}>click to browse</Box></Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "center" }}>
                {["Name", "Email", "Phone Number", "Amount"].map((c) => <Chip key={c} label={c} size="small" sx={{ fontSize: "0.68rem", height: 20, fontFamily: "monospace" }} />)}
              </Box>
            </Box>
          </Box>
        )}

        {parseResult && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: 90, p: 1.5, borderRadius: 2, bgcolor: "success.50", border: "1px solid", borderColor: "success.200", textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: "success.main", lineHeight: 1 }}>{totalReady}</Typography>
                <Typography variant="caption" color="success.dark" sx={{ fontWeight: 700 }}>Ready</Typography>
              </Box>
              {totalMissing > 0 && (
                <Box sx={{ flex: 1, minWidth: 90, p: 1.5, borderRadius: 2, bgcolor: "warning.50", border: "1px solid", borderColor: "warning.200", textAlign: "center" }}>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: "warning.main", lineHeight: 1 }}>{totalMissing}</Typography>
                  <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 700 }}>Need amount</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 90, p: 1.5, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200", textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>${grandTotal.toFixed(2)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Total</Typography>
              </Box>
            </Box>

            {parseErrors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                {parseErrors.slice(0, 4).map((e, i) => <Typography key={i} variant="caption" sx={{ display: "block" }}>• {e}</Typography>)}
              </Alert>
            )}
            {totalMissing > 0 && <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}><Typography variant="body2" sx={{ fontWeight: 700 }}>Fill in missing amounts below.</Typography></Alert>}

            {editedRows.length > 0 && (
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 2, maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["Name", "Contact", "Type", "Amount"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.06em", bgcolor: "grey.50", color: "text.secondary" }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editedRows.map((row, i) => (
                      <TableRow key={i} sx={{ bgcolor: !row.amount || parseFloat(row.amount) <= 0 ? "warning.50" : "transparent" }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: "0.82rem" }}>{row.name}</TableCell>
                        <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.contact}</TableCell>
                        <TableCell>
                          <Chip label={row.contactType === "email" ? "Email" : "SMS"} size="small"
                            icon={row.contactType === "email" ? <EmailIcon sx={{ fontSize: "10px !important" }} /> : <PhoneIcon sx={{ fontSize: "10px !important" }} />}
                            color={row.contactType === "email" ? "info" : "success"} variant="outlined" sx={{ fontSize: "0.63rem", height: 17 }} />
                        </TableCell>
                        <TableCell sx={{ minWidth: 105 }}>
                          {!row.amount || parseFloat(row.amount) <= 0
                            ? <TextField size="small" type="number" placeholder="0.00" value={row.amount} onChange={(e) => updateRowAmount(i, e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="caption" fontWeight={700}>$</Typography></InputAdornment> }} sx={{ width: 95, "& input": { fontSize: "0.78rem", py: 0.5 } }} />
                            : <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><Typography sx={{ fontWeight: 700, fontSize: "0.82rem", color: "success.main" }}>${parseFloat(row.amount).toFixed(2)}</Typography><IconButton size="small" sx={{ p: 0.25 }} onClick={() => updateRowAmount(i, "")}><EditNoteIcon sx={{ fontSize: 12, color: "text.disabled" }} /></IconButton></Box>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Button size="small" variant="text" startIcon={<UploadFileIcon sx={{ fontSize: 13 }} />} onClick={() => { setParseResult(null); setEditedRows([]); setFileName(""); }} sx={{ fontSize: "0.75rem", textTransform: "none", color: "text.secondary" }}>Upload different file</Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.200", gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, minWidth: 80 }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!editedRows.length || totalMissing > 0} endIcon={<ArrowForwardIcon />} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, minWidth: 210 }}>
          {totalMissing > 0 ? `Fill in ${totalMissing} missing amount${totalMissing !== 1 ? "s" : ""}` : `Import ${editedRows.length} recipient${editedRows.length !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}